import { cloneElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, Button, DialogContent, Typography, alpha } from '@mui/material'

import { CheckCircle, Cancel, Error } from '@mui/icons-material'
import { colors } from '@/theme/index.theme'
import { FormStatusType } from '@/enums'

import { DialogModel } from '@/state/ui/global/models'

export const DialogFeedbackComponent = ({
  feedback,
  id,
  open,
}: DialogModel) => {
  const { t } = useTranslation()

  const {
    variant,
    buttonPrimaryAction,
    buttonSecondaryAction,
    icon,
    maxWidth,
    formStatus,
    item,
    errorMessage,
  } = feedback || {}

  const success = variant === 'success'

  const icons = {
    success: { icon: <CheckCircle />, color: colors.green },
    error: { icon: <Cancel />, color: colors.red },
    warning: { icon: <Error />, color: colors.orange },
  }

  const what =
    formStatus === FormStatusType.CREATE
      ? t('messages.created')
      : formStatus === FormStatusType.EDIT
      ? t('messages.update')
      : t('messages.duplicated')

  const title = () => {
    if (feedback?.title) {
      return feedback?.title
    }

    if (success) {
      if (formStatus === FormStatusType.CREATE) {
        return t('labels.createdSuccessfully')
      }

      if (formStatus === FormStatusType.EDIT) {
        return t('labels.updatedSuccessfully')
      }

      return t('labels.duplicatedSuccessfully')
    } else {
      if (formStatus === FormStatusType.CREATE) {
        return t('error.creationFailed')
      }

      if (formStatus === FormStatusType.EDIT) {
        return t('error.updateFailed')
      }

      return t('error.duplicateFailed')
    }
  }

  const subtitle =
    feedback?.subtitle ||
    (success
      ? t('messages.successfully', {
          item,
          what,
        })
      : errorMessage ||
        t('error.notSuccessfully', {
          item,
          what,
        }))

  const buttonPrimaryText =
    feedback?.buttonPrimaryText ||
    (success ? t('labels.goToTheLibrary') : t('labels.tryAgain'))

  const buttonSecondaryText =
    feedback?.buttonSecondaryText ||
    (success ? t('messages.stayOnThisPage') : '')

  return (
    <Dialog
      disableEnforceFocus
      fullWidth
      open={id === 'DIALOG_FEEDBACK' && open}
      PaperProps={{
        sx: { maxWidth: maxWidth || 400 },
      }}
    >
      <DialogContent
        sx={{
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '40px 56px',
        }}
      >
        {variant &&
          cloneElement(icon || icons[variant].icon, {
            sx: {
              mt: 2,
              mb: 4,
              fontSize: 48,
              color: icons[variant].color,
              borderRadius: '50%',
              boxShadow: `0px 0px 0px 16px ${alpha(icons[variant].color, 0.1)}`,
              bgcolor: alpha(icons[variant].color, 0.1),
            },
          })}
        <Typography variant="h5" sx={{ my: 2 }} fontWeight={700}>
          {title()}
        </Typography>
        {typeof subtitle === 'string' ? (
          <Typography variant="body2">{subtitle}</Typography>
        ) : (
          subtitle
        )}
        <Button
          variant="contained"
          size="small"
          onClick={buttonPrimaryAction}
          sx={{ mt: 4, mb: 2 }}
        >
          {buttonPrimaryText}
        </Button>
        {buttonSecondaryAction && (
          <Button
            variant="text"
            size="small"
            className="white"
            onClick={buttonSecondaryAction}
          >
            {buttonSecondaryText}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  )
}
